import { useState, useEffect } from "react";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import Sort from "../Common/Sort/Sort";
import { newsTypes, newsCountProps } from "./newsContent.types";
import rightArrow from "../../assets/images/icons/rightArrow.png";
import useWindowDimemsions from "../../hooks/useWindowDimesnions";
import CardSearch from "../Common/CardSearch";
import { getSortOrFilteredArr, getUniqueOptions } from "../../utils/Common";
import "./newsContent.scss";
import { styled, alpha } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import { customSortArr } from "../../utils/Sort";
/**
 * NewsContent Component.
 *
 * This component displays a list of news articles with options for sorting and filtering.
 * Users can sort news articles by date and sentiment, and filter them by news source.
 *
 * This function in called in \src\Views\News\News.tsx
 *
 * @template T - The type parameter for news data.
 * @param {Object} props - The props containing setSingleNews and allNews information.
 * @returns {JSX.Element} - A React JSX element representing the NewsContent component.
 */
const NewsContent = <T extends {}>({
  setSingleNews,
  allNews,
}: newsCountProps<T>) => {
  // Initialize search parameters from URL
  const [searchParams, setSearchParams] = useSearchParams();
  // State for the list of news
  const [newsList, setNewsList] = useState<newsTypes[] | []>([]);
  // State for the dropdown open status
  const [open, setOpen] = useState(false);
  // State for the search value
  const [searchValue, setSearchValue] = useState<any>(null);
  // State for the sort value
  const [sortValue, setSortValue] = useState("");
  // State for the original data
  const [originalData, setOriginalData] = useState<newsTypes[] | []>([]);
  // State for the options data
  const [optionsData, setOptionsData] = useState<any[]>([]);

  const [openSearch, setOpenSearch] = useState(false);

  // Array of sorting options
  const sortSentiment = [
    "Date: Newest to Oldest",
    "Date: Oldest to Newest",
    "Sentiment: Positive to Negative",
    "Sentiment: Negative to Positive",
  ];

  // Function to update sort value and set it in search parameters
  const updatesetSortValue = (value: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("sort", value);
      return params;
    });
    setSortValue(value);
  };

  // Effect to reset states and set sort value from search parameters when allNews or searchParams change
  useEffect(() => {
    setNewsList(allNews);
    setOriginalData(allNews);
    setOptionsData(getUniqueOptions(allNews));
    setSearchValue(null);
    const params = new URLSearchParams(searchParams);
    const sort = params.get("sort");
    if (sort) {
      setSortValue(sort);
    }
  }, [allNews, searchParams]);

  // Effect to set newsList based on search and sort values when they change
  useEffect(() => {
    let result = originalData;
    
    // First apply text search (title/source)
    if (searchValue && searchValue.trim() !== "") {
      result = result.filter((news) =>
        news.News_Title.toLowerCase().includes(searchValue.toLowerCase()) ||
        news.News_Source.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    // Then apply sorting
    if (sortValue) {
      result = customSortArr(result, sortValue, 'News_date', 'News_Sentiment');
    }
    
    setNewsList(result);
  }, [searchValue, sortValue, originalData]);

  const { width }: any = useWindowDimemsions();

  const Search = styled("div")(({ theme }) => ({
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    display: "flex",
    alignItems: "center",
    transition: "width 0.3s ease",
  }));

  const SearchIconWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: "100%",
    position: "absolute",
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  }));

  const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: "inherit",
    width: "100%",
    "& .MuiInputBase-input": {
      padding: theme.spacing(1, 1, 1, 0),
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create("width"),
    },
  }));

  // const xsWidth = 576;
  // const heightStyle =
  //   width && width < xsWidth && newsList?.length === 0
  //     ? { minHeight: "100px", maxHeight: "100px" }
  //     : width && width < xsWidth && newsList?.length > 0
  //       ? { maxHeight: "75vh" }
  //       : { minHeight: "75vh", maxHeight: "75vh" };

  // console.log('state', newsList.map((d:any)=>d.News_date));

  console.log({ allNews });

  return (
    <div className="news-content">
      <div className="inner-content">
        <div className="header">
          {/* Title + count */}
          <div className="nc-title-wrap">
            <h6 className="nc-title">News</h6>
            {newsList.length > 0 && (
              <span className="nc-count">{newsList.length}</span>
            )}
          </div>

          {/* Right controls */}
          <div className="header-right">
            {/* Inline search */}
            <div className={`nc-search-wrap ${openSearch ? "nc-search-wrap--open" : ""}`}>
              {openSearch ? (
                <>
                  <SearchIcon className="nc-search-icon-inline" sx={{ fontSize: 16 }} />
                  <input
                    autoFocus
                    className="nc-search-input"
                    placeholder="Search title or source…"
                    value={searchValue || ""}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onBlur={() => { if (!searchValue) setOpenSearch(false); }}
                  />
                  {searchValue && (
                    <button
                      className="nc-search-clear"
                      onMouseDown={() => { setSearchValue(""); setOpenSearch(false); }}
                    >&#10005;</button>
                  )}
                </>
              ) : (
                <button className="nc-icon-btn" onClick={() => setOpenSearch(true)} title="Search">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </button>
              )}
            </div>

            {/* Sort */}
            <Sort
              sortSentiment={sortSentiment}
              sortByScore={(value: string) => updatesetSortValue(value)}
              title="Sort"
            />
          </div>
        </div>
        <div className="insider-child">
          {(newsList as [])?.length > 0 &&
            (newsList as []).map((news: newsTypes, index) => {
              const sentiment = news.News_Sentiment?.toLowerCase();
              const sentimentColor =
                sentiment === "negative" ? "#dc2626"
                : sentiment === "positive" ? "#16a34a"
                : "#d97706";
              const sentimentBg =
                sentiment === "negative" ? "#fff1f2"
                : sentiment === "positive" ? "#f0fdf4"
                : "#fffbeb";
              const sentimentBorder =
                sentiment === "negative" ? "#fecdd3"
                : sentiment === "positive" ? "#bbf7d0"
                : "#fde68a";

              return (
                <div
                  key={index}
                  className={`news-item ${news.active === true ? "active" : ""}`}
                  style={{ borderLeftColor: sentimentColor }}
                  onClick={() => {
                    setSingleNews(news);
                    const copyNewsList = newsList.map((element, ind) =>
                      ind === index
                        ? { ...element, active: true }
                        : { ...element, active: false },
                    );
                    setNewsList(() => copyNewsList);
                  }}
                >
                  {/* Top row: source + sentiment */}
                  <div className="news-item-top">
                    {news.News_Source && (
                      <span className="news-source-label">{news.News_Source}</span>
                    )}
                    <div className="news-sentiment-right">
                      <span className="news-sentiment-text" style={{ color: sentimentColor }}>
                        {news.News_Sentiment
                          ? news.News_Sentiment.charAt(0).toUpperCase() + news.News_Sentiment.slice(1)
                          : "Neutral"}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <p className="news-item-title">{news.News_Title}</p>

                  {/* Date */}
                  {news.News_date && (
                    <div className="news-item-date">
                      {moment(news.News_date).format("DD MMM YYYY, HH:mm")}
                    </div>
                  )}
                </div>
              );
            })}

          {newsList.length === 0 && (
            <p style={{ textAlign: "center", color: "#b0b7c3", fontSize: "13px", marginTop: "2rem" }}>
              No News
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsContent;
